import {browser} from '$app/environment';
import {createMachine, assign, interpret} from 'xstate';
import type {
  BaseActionObject,
  MachineConfig,
  MachineOptions,
  StateMachine,
  StateSchema,
} from 'xstate/lib/types';

import netlifyIdentity from 'netlify-identity-widget';
import type {Organization} from '$types/entities/organization';

import {AUTH_USER_KEY} from '$lib/constants/local-storage';

export enum EventType {
  IdentityInit = 'IDENTITY_INIT_EVENT',
  IdentityLogin = 'IDENTITY_LOGIN_EVENT',
  IdentityLogout = 'IDENTITY_LOGOUT_EVENT',
  IdentityError = 'IDENTITY_ERROR_EVENT',
  RequestLogout = 'LOGOUT',
}

interface AuthStateSchema extends StateSchema {
  states: {
    initial: StateSchema;
    unknown: StateSchema;
    authd: StateSchema;
    loggingOut: StateSchema;
    notAuthd: StateSchema;
  };
}

interface IdentityInitEvent extends BaseActionObject {
  type: EventType.IdentityInit;
  data?: netlifyIdentity.User;
}

export interface IdentityLoginEvent extends BaseActionObject {
  type: EventType.IdentityLogin;
  data: netlifyIdentity.User;
}

interface IdentityLogoutEvent extends BaseActionObject {
  type: EventType.IdentityLogout;
}

interface RequestLogoutEvent extends BaseActionObject {
  type: EventType.RequestLogout;
}

interface IdentityErrorEvent extends BaseActionObject {
  type: EventType.IdentityError;
  data: Error;
}

type AuthMachineEvent =
  | IdentityInitEvent
  | IdentityLoginEvent
  | IdentityLogoutEvent
  | RequestLogoutEvent
  | IdentityErrorEvent;

interface AuthMachineContext {
  user?: netlifyIdentity.User;
  organizations: Organization[];
  error?: Error;
}

type AuthStateMachine = StateMachine<
  AuthMachineContext,
  AuthStateSchema,
  AuthMachineEvent
>;

type AuthMachineConfig = MachineConfig<
  AuthMachineContext,
  AuthStateSchema,
  AuthMachineEvent
>;

export type AuthMachineOptions = Partial<
  MachineOptions<AuthMachineContext, AuthMachineEvent>
>;

const config: AuthMachineConfig = {
  predictableActionArguments: true,

  id: 'auth',

  initial: 'initial',

  context: {
    organizations: [],
  },

  on: {
    [EventType.IdentityInit]: [
      {
        cond: 'isAuthd',
        target: 'authd',
        actions: ['handleUser', 'setLsAuthUser'],
      },
      {target: 'notAuthd'},
    ],
    [EventType.IdentityError]: {actions: ['handleError']},
  },

  states: {
    initial: {
      invoke: {
        src: 'netlifySetup',
        onDone: {target: 'unknown'},
        onError: {target: 'unknown'},
      },
    },

    unknown: {
      invoke: {
        src: 'initNetlify',
        onDone: [
          {
            cond: 'hasLsAuthUser',
            target: 'authd',
          },
          {target: 'notAuthd'},
        ],
        onError: [
          {
            cond: 'hasLsAuthUser',
            target: 'authd',
          },
          {target: 'notAuthd'},
        ],
      },
    },

    authd: {
      on: {
        [EventType.IdentityLogout]: {target: 'notAuthd'},
        [EventType.RequestLogout]: {target: 'loggingOut'},
      },
    },

    notAuthd: {
      entry: ['clearContext', 'clearLs'],

      on: {
        [EventType.IdentityLogin]: {
          target: 'authd',
          actions: ['handleUser', 'setLsAuthUser'],
        },
      },
    },

    loggingOut: {
      invoke: {
        src: 'logout',
        onDone: {
          target: 'notAuthd',
        },
        onError: {target: 'unknown'},
      },
    },
  },
};

const options: AuthMachineOptions = {
  actions: {
    handleError: assign((_context, event) => {
      const {data} = event as IdentityErrorEvent;

      return {error: data};
    }),

    handleUser: assign((_context, event) => {
      const {data} = event as IdentityInitEvent | IdentityLoginEvent;

      return {user: data};
    }),

    clearContext: assign({organizations: []}),

    setLsAuthUser: (_context, event) => {
      const {data: user} = event as IdentityLoginEvent;

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      }
    },

    clearLs: () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_USER_KEY);
      }
    },
  },

  guards: {
    hasLsAuthUser: () => {
      let result = false;

      if (typeof localStorage !== 'undefined') {
        result = Boolean(localStorage.getItem(AUTH_USER_KEY));
      }

      return result;
    },

    isAuthd: (_, event) => {
      const {data: user} = event as IdentityInitEvent;

      return Boolean(user);
    },
  },

  services: {
    // TODO: LB - move to its own machine with states
    // 'notInitialised' | 'initialised', and send events to the parent machine
    // when events are triggered by Netlify
    netlifySetup: () => (callback) => {
      async function handleIdentityInit(user?: netlifyIdentity.User) {
        let updatedUser = null;

        if (user) {
          await netlifyIdentity.refresh();
          updatedUser = netlifyIdentity.currentUser();
        }

        callback({type: EventType.IdentityInit, data: updatedUser});
      }

      async function handleIdentityLogin(_user: netlifyIdentity.User) {
        await netlifyIdentity.refresh();
        const user = netlifyIdentity.currentUser();

        callback({type: EventType.IdentityLogin, data: user});
      }

      function handleIdentityLogout() {
        callback(EventType.IdentityLogout);
      }

      function handleIdentityError(error: Error) {
        callback({type: EventType.IdentityError, data: error});
      }

      const promise = new Promise((resolve) => {
        if (!browser) {
          resolve({});

          return;
        }

        netlifyIdentity.on('init', handleIdentityInit);
        netlifyIdentity.on('login', handleIdentityLogin);
        netlifyIdentity.on('logout', handleIdentityLogout);
        netlifyIdentity.on('error', handleIdentityError);

        resolve({});
      });

      return promise;
    },

    initNetlify: () => {
      const promise = new Promise((resolve) => {
        if (browser) {
          netlifyIdentity.init({logo: false});
        }
        resolve({});
      });

      return promise;
    },
    logout: async () => {
      return await netlifyIdentity.logout();
    },
  },
};

const machine = createMachine(config, options) as AuthStateMachine;

export {machine};
